import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Download, Loader2, Images, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { savePendingAction } from '../../utils/pendingAction';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { mediaUrl } from '../../services/postsApi';
import ShareVerseButton from './ShareVerseButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE = 24;
const FILTERS = [['all', 'All', 'Todas'], ['en', 'English', 'Inglés'], ['es', 'Español', 'Español'], ['fav', 'My Favorites', 'Mis favoritas']];

const fmtDate = (d, es) => {
  if (!d) return '';
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString(es ? 'es-ES' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

// Scrollable archive of the daily verse images — save or share any of them.
export const VerseGalleryTab = ({ language }) => {
  const es = language === 'es';
  const [lang, setLang] = useState('all');
  const [items, setItems] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const filterRef = useRef('all');
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async (from, filter) => {
    setLoading(true);
    try {
      const params = filter === 'fav' ? { favorites: true, skip: from, limit: PAGE } : { lang: filter, skip: from, limit: PAGE };
      const r = await axios.get(`${BACKEND_URL}/api/tools/votd/gallery`, { params, withCredentials: true });
      if (filterRef.current !== filter) return;  // a newer filter is active
      setItems((prev) => (from === 0 ? r.data.items : [...prev, ...r.data.items]));
      setHasMore(!!r.data.has_more);
      setSkip(from + PAGE);
    } catch {
      if (from === 0) setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { filterRef.current = lang; load(0, lang); }, [lang, load]);

  const toggleFav = async (it, e) => {
    e?.stopPropagation();
    if (!user) {
      savePendingAction({ returnTo: '/community?tab=gallery' });
      toast(es ? 'Inicia sesión para guardar favoritas' : 'Sign in to save favorites');
      navigate('/login');
      return;
    }
    const next = !it.favorited;
    const apply = (v) => {
      setItems((prev) => (lang === 'fav' && !v ? prev.filter((x) => x.id !== it.id) : prev.map((x) => (x.id === it.id ? { ...x, favorited: v } : x))));
      setOpen((o) => (o && o.id === it.id ? { ...o, favorited: v } : o));
    };
    apply(next);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/tools/votd/gallery/${encodeURIComponent(it.id)}/favorite`,
        { image_url: it.image_url, reference: it.reference, date: it.date, lang: it.lang }, { withCredentials: true });
      if (r.data.favorited !== next) apply(r.data.favorited);
    } catch {
      apply(!next);
      toast.error(es ? 'No se pudo guardar' : 'Could not update favorite');
    }
  };

  const save = async (it) => {
    try {
      const res = await fetch(mediaUrl(it.image_url));
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `lumera-${it.date || 'verse'}-${it.lang}.jpg`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch {
      toast.error(es ? 'No se pudo guardar la imagen' : 'Could not save the image');
    }
  };

  return (
    <div data-testid="verse-gallery">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Images className="w-5 h-5 text-primary" />{es ? 'Galería de versículos' : 'Verse Art Gallery'}</h2>
          <p className="text-sm text-muted-foreground">{es ? 'Guarda o comparte cualquier versículo del día.' : 'Save or share any past verse of the day.'}</p>
        </div>
        <div className="inline-flex rounded-full border border-border bg-muted/40 p-1" data-testid="verse-gallery-lang-filter">
          {FILTERS.filter(([id]) => id !== 'fav' || user).map(([id, en, esl]) => (
            <button key={id} type="button" onClick={() => setLang(id)} data-testid={`verse-gallery-lang-${id}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${lang === id ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              {es ? esl : en}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 [grid-template-columns:repeat(2,minmax(0,1fr))] sm:[grid-template-columns:repeat(3,minmax(0,1fr))] lg:[grid-template-columns:repeat(4,minmax(0,1fr))]">
        {items.map((it, i) => (
          <button key={it.id} type="button" onClick={() => setOpen(it)} data-testid={`verse-gallery-item-${i}`}
            className="group relative aspect-square overflow-hidden rounded-xl bg-muted focus:outline-none focus:ring-2 focus:ring-primary">
            <img src={mediaUrl(it.image_url)} alt={it.reference || 'verse'} loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
            <span role="button" tabIndex={0} onClick={(e) => toggleFav(it, e)} onKeyDown={(e) => e.key === 'Enter' && toggleFav(it, e)}
              aria-label={es ? 'Favorita' : 'Favorite'} data-testid={`verse-gallery-fav-${i}`}
              className="absolute top-2 right-2 rounded-full bg-black/45 p-1.5 backdrop-blur-sm transition-transform hover:scale-110">
              <Heart className={`w-4 h-4 ${it.favorited ? 'fill-rose-500 text-rose-500' : 'text-white'}`} />
            </span>
            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-left text-[11px] text-white">
              {fmtDate(it.date, es)} · {it.lang.toUpperCase()}
            </span>
          </button>
        ))}
      </div>

      {loading && <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
      {!loading && items.length === 0 && (
        <p className="py-12 text-center text-sm text-muted-foreground" data-testid="verse-gallery-empty">
          {lang === 'fav' ? (es ? 'Toca el corazón en una imagen para guardarla aquí.' : 'Tap the heart on any image to keep it here.') : (es ? 'Aún no hay imágenes.' : 'No images yet.')}
        </p>
      )}
      {!loading && hasMore && (
        <div className="flex justify-center mt-4">
          <Button variant="outline" onClick={() => load(skip, lang)} data-testid="verse-gallery-load-more">{es ? 'Cargar más' : 'Load more'}</Button>
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-lg p-3" data-testid="verse-gallery-viewer">
          <DialogTitle className="sr-only">{open?.reference}</DialogTitle>
          {open && (
            <>
              <img src={mediaUrl(open.image_url)} alt={open.reference || 'verse'} className="w-full rounded-lg" />
              <div className="flex items-center gap-3 pt-2 px-1">
                <span className="text-sm font-medium">{open.reference} <span className="text-muted-foreground font-normal">· {fmtDate(open.date, es)}</span></span>
                <Button size="sm" variant="ghost" onClick={() => toggleFav(open)} className="ml-auto" data-testid="verse-gallery-viewer-fav"
                  aria-label={es ? 'Favorita' : 'Favorite'}>
                  <Heart className={`w-4 h-4 ${open.favorited ? 'fill-rose-500 text-rose-500' : ''}`} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => save(open)} data-testid="verse-gallery-save">
                  <Download className="w-4 h-4 mr-1" />{es ? 'Guardar' : 'Save'}
                </Button>
                <ShareVerseButton post={{ id: open.id, image_url: open.image_url, verse_reference: open.reference }} es={es} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VerseGalleryTab;
